import {ApolloClient, HttpLink} from "@apollo/client"
import {removeTypenameFromVariables} from "@apollo/client/link/remove-typename"

import {InMemoryCache} from "@apollo/client/cache"
import {onError} from "@apollo/client/link/error"
import buildAxiosFetch from "../../axios-fetch"
import graphFetch from "../graph/fetch"
import {getUrl} from "../graph"
import typeDefs from "./schema"
import {apolloLogger} from "./middleware/apolloLogger"
import {apolloErrorHandlerLink} from "./middleware/errorHandler"

export const cache = new InMemoryCache({
  // By default apollo looks for `id` and `_id` but we use `uuid` a lot
  dataIdFromObject: (obj) =>
    !obj.uuid && !obj.id
      ? false // missing cacheable keys so store in parent
      : obj.__typename + ":" + (obj.uuid || obj.id),

  /*
   * Control how types are stored in cache
   *
   * This is only needed if the type returned lacks a `uuid` or `id` field,
   * which is extracted via the `dataIdFromObject` function above
   *
   * @see https://www.apollographql.com/docs/react/caching/cache-configuration#customizing-cache-ids
   */
  typePolicies: {
    // don't cache products for reasons TODO: what are reasons?
    Product: {keyFields: false},

    // don't cache snapshots for reasons TODO: what are reasons?
    DealProductSnapshot: {keyFields: false},

    // budget uuids are per budget, but they are specific to each wallet
    Budget: {keyFields: ["walletId", "uuid"]},
    BudgetUnassigned: {keyFields: ["walletId", "uuid"]},

    Query: {
      /* @see https://www.apollographql.com/docs/react/caching/advanced-topics#cache-redirects */
      fields: {
        poll: {read: (_, {args, toReference}) => toReference({uuid: args?.id, __typename: "Poll"})},
        recognition: {read: (_, {args, toReference}) => toReference({uuid: args?.id, __typename: "Recognition"})}
      }
    },
    // @see https://www.apollographql.com/docs/react/caching/cache-field-behavior#merging-arrays
    Payment: {
      fields: {
        details: {
          merge(existing: any = {}, incoming: any) {
            return {...existing, ...incoming}
          }
        }
      }
    },
    CheckoutRedemption: {
      fields: {
        order: {
          // @see https://www.apollographql.com/docs/react/caching/cache-field-behavior#merging-non-normalized-objects
          merge(existing = {}, incoming, {mergeObjects}) {
            return mergeObjects(existing, incoming)
          }
        }
      }
    }
  }
})

/**
 * The wrapper Error that will be returned from any Apollo component
 * or hook
 */
export interface PBClientApolloError extends Error {
  graphQLErrors: []
  networkError: GraphError
}

const client = new ApolloClient({
  // an apollo 'link' is like middleware, you build up a link of
  // functions and the requests flow through and then return back
  // along the link, so order is important!
  link:
    // NOTE: inlining because it's important
    // this must come first to capture any errors that occured and
    // feed them down into components or hooks
    // this will return any errors in a PBClientApolloError
    //
    // See the apolloErrorHandler for how we throw an errors the occurred
    // further up the link
    onError(({graphQLErrors, networkError}) => {
      if (graphQLErrors) {
        // empty for now
      }

      if (networkError) {
        console.log("[Network error]:", networkError)
      }
    })
      .concat(apolloErrorHandlerLink)
      .concat(apolloLogger())
      .concat(removeTypenameFromVariables())
      .concat(
        new HttpLink({
          // Need to make sure we re-evaluate region url on each request
          uri: (op) => getUrl({reqEUinstance: op.getContext()?.reqEUinstance}),
          fetch: buildAxiosFetch(graphFetch())
        })
      ),
  cache,
  typeDefs
})

export default client
